/**
 * Vigencia del test de idoneidad en el alta del plan (paso 2.2.2 / 2.4.3).
 * Espejo de SuitabilityTestValidityRules y PlanRiskProfileRules.GetLatestSignedSuitabilityProfileId
 * del backend (IroniaBackendCore): un test SIGNED caduca a los 2 años de su firma (decisión de
 * negocio 2026-10-20). Un test caducado no cuenta para completar el paso ni para evaluar el riesgo.
 * Si se cambia la regla aquí hay que cambiarla también en el backend, que es quien manda.
 */
import type { IAnswer, IQuestion, IQuestionAnswer, IQuestionTableCol, ISuitabilityTest, ISuitabilityTestVersion } from "@/services/SuitabilityTestsService";

/** Años que dura un test de idoneidad firmado (SuitabilityTestValidityRules.ValidityYears). */
export const SUITABILITY_TEST_VALIDITY_YEARS = 2;

/** Días de la ventana en la que no se firman más de dos tests (SuitabilityTestValidityRules.ResignWindowDays). */
export const SUITABILITY_TEST_RESIGN_WINDOW_DAYS = 30;

function toDate(value: string | Date | null | undefined): Date | null
{
    if(value == null || value === "")
    {
        return null;
    }

    const date = value instanceof Date ? new Date(value.getTime()) : new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
}

// Igual que DateTime.AddYears de .NET: si el día no existe en el año destino (29 de febrero)
// se queda en el último día del mes, en lugar de saltar al 1 de marzo como hace setFullYear.
function addYears(date: Date, years: number): Date
{
    const result = new Date(date.getTime());
    const month = result.getMonth();
    result.setFullYear(result.getFullYear() + years);
    if(result.getMonth() !== month)
    {
        // Día 0 del mes siguiente = último día del mes original.
        result.setDate(0);
    }
    return result;
}

/**
 * Fecha de firma del test: la de su firma OTP si viene cargada y, si no, la de su última
 * modificación (cuando se marcó como firmado) o, en su defecto, la de creación.
 * Devuelve null si ninguna fecha es válida.
 */
export function getSuitabilityTestSignedDate(test: ISuitabilityTest | null | undefined): Date | null
{
    if(!test)
    {
        return null;
    }

    // Mismo orden de preferencia que el backend (?? encadenado): solo se pasa a la siguiente
    // fecha cuando la anterior no viene informada.
    return toDate(test.signature?.signAt ?? test.updatedAt ?? test.createdAt);
}

/** Fecha de caducidad: la de firma más los años de vigencia, con el mismo addYears que usa la regla. */
export function getSuitabilityTestExpiresAt(test: ISuitabilityTest | null | undefined): Date | null
{
    const signedDate = getSuitabilityTestSignedDate(test);
    if(!signedDate)
    {
        return null;
    }

    return addYears(signedDate, SUITABILITY_TEST_VALIDITY_YEARS);
}

/**
 * Indica si el test está firmado y dentro de su vigencia (fecha de firma + 2 años > ahora).
 * Si llega la versión actual, un test de otra versión no está vigente. Sin ese id no se mira la versión,
 * igual que IsInForce del backend.
 */
export function isSuitabilityTestInForce(
    test: ISuitabilityTest | null | undefined,
    now: Date = new Date(),
    currentVersionId?: number | null
): boolean
{
    if(!test || test.status !== "SIGNED")
    {
        return false;
    }

    if(currentVersionId != null && test.suitabilityTestVersionId !== currentVersionId)
    {
        return false;
    }

    const signedDate = getSuitabilityTestSignedDate(test);
    if(!signedDate)
    {
        return false;
    }

    return addYears(signedDate, SUITABILITY_TEST_VALIDITY_YEARS).getTime() > now.getTime();
}

function addDays(date: Date, days: number): Date
{
    const result = new Date(date.getTime());
    result.setDate(result.getDate() + days);
    return result;
}

/**
 * Fecha en la que se levanta el bloqueo de los 30 días, o null si no hay bloqueo.
 * El paso 2.2.2 no recibe SuitabilityTestNextUpdateAt: el botón lo apaga el servidor y la fecha
 * se calcula aquí con la misma ventana, solo para el texto.
 * Una versión distinta de la actual no bloquea (V6).
 */
export function getSuitabilityNextUpdateAt(
    tests: ISuitabilityTest[] | null | undefined,
    now: Date = new Date(),
    currentVersionId?: number | null
): Date | null
{
    const signed = (tests ?? []).filter((test) => test?.status === "SIGNED");
    const latest = getLatestSignedSuitabilityTest(signed);
    if(latest && currentVersionId != null && latest.suitabilityTestVersionId !== currentVersionId)
    {
        return null;
    }

    const inWindow = signed
        .map((test) => ({test: test, signedDate: getSuitabilityTestSignedDate(test)}))
        .filter((item): item is {test: ISuitabilityTest; signedDate: Date} =>
        {
            return item.signedDate != null
                && addDays(item.signedDate, SUITABILITY_TEST_RESIGN_WINDOW_DAYS).getTime() > now.getTime();
        })
        .sort((left, right) =>
        {
            const difference = right.signedDate.getTime() - left.signedDate.getTime();
            if(difference !== 0)
            {
                return difference;
            }

            return (right.test.id ?? 0) - (left.test.id ?? 0);
        });

    if(inWindow.length < 2)
    {
        return null;
    }

    return addDays(inWindow[1].signedDate, SUITABILITY_TEST_RESIGN_WINDOW_DAYS);
}

/** Tests firmados y vigentes de la lista. */
export function getInForceSuitabilityTests(
    tests: ISuitabilityTest[] | null | undefined,
    now: Date = new Date(),
    currentVersionId?: number | null
): ISuitabilityTest[]
{
    return (tests || []).filter((test) => isSuitabilityTestInForce(test, now, currentVersionId));
}

/**
 * Hay al menos un test firmado pero ninguno vigente: todos los firmados han caducado.
 * Sirve para mostrar "Caducado" en lugar de "No" en la tabla de idoneidad.
 */
export function hasOnlyExpiredSignedSuitabilityTests(
    tests: ISuitabilityTest[] | null | undefined,
    now: Date = new Date(),
    currentVersionId?: number | null
): boolean
{
    const list = tests || [];
    return list.some((test) => test?.status === "SIGNED")
        && !list.some((test) => isSuitabilityTestInForce(test, now, currentVersionId));
}

/**
 * Test firmado y vigente más reciente con perfil en results (results.results.profile).
 * El más reciente es el de fecha de firma más tardía; a igualdad, el de id mayor. No se usa
 * la posición en la lista, que depende del orden en que la base de datos devuelve los tests.
 */
export function getLatestInForceSignedTestWithProfile(tests: ISuitabilityTest[] | null | undefined, now: Date = new Date()): ISuitabilityTest | null
{
    let latest: ISuitabilityTest | null = null;
    let latestTime = Number.NEGATIVE_INFINITY;

    getInForceSuitabilityTests(tests, now).forEach((test) =>
    {
        if(test.results?.results?.profile == null)
        {
            return;
        }

        // isSuitabilityTestInForce ya garantiza que la fecha es válida.
        const time = (getSuitabilityTestSignedDate(test) as Date).getTime();
        if(latest === null
            || time > latestTime
            || (time === latestTime && (test.id ?? 0) > (latest.id ?? 0)))
        {
            latest = test;
            latestTime = time;
        }
    });

    return latest;
}

/**
 * Test firmado más reciente, vigente o caducado. El más reciente es el de fecha de firma más
 * tardía; a igualdad, el de id mayor. No se usa la posición en la lista. Un borrador no cuenta.
 * No filtra por vigencia: en la renovación el que interesa es justo el caducado.
 */
export function getLatestSignedSuitabilityTest(tests: ISuitabilityTest[] | null | undefined): ISuitabilityTest | null
{
    let latest: ISuitabilityTest | null = null;
    let latestTime = Number.NEGATIVE_INFINITY;

    (tests ?? []).forEach((test) =>
    {
        if(test?.status !== "SIGNED")
        {
            return;
        }

        const signedDate = getSuitabilityTestSignedDate(test);
        const time = signedDate ? signedDate.getTime() : Number.NEGATIVE_INFINITY;
        const latestId = latest?.id ?? 0;
        const testId = test.id ?? 0;
        if(latest === null || time > latestTime || (time === latestTime && testId > latestId))
        {
            latest = test;
            latestTime = time;
        }
    });

    return latest;
}

/** Aviso cuando el test de partida es de otra versión del cuestionario. Lo pinta la pantalla de renovación. */
export const SuitabilityQuestionnaireChangedMessage = "El cuestionario ha cambiado desde tu último test; revisa las preguntas sin contestar";

export interface SuitabilityAnswersMatch
{
    answers: IAnswer[];
    questionnaireChanged: boolean;
}

/**
 * Los `_id` de pregunta y de opción no se conservan cuando Finametrix publica otro cuestionario
 * (comprobado en dev: 14 versiones, 0 ids compartidos entre versiones consecutivas salvo una copia).
 * `key` tampoco sirve para casar: es la categoría (suitability, convenience) y la comparten varias
 * preguntas. Se casa la pregunta por tipo, key, título, texto y textos de las opciones, y la opción
 * por su `_id` si sigue existiendo o, si no, por value y order. Lo que no casa se descarta.
 */
export function matchSuitabilityAnswersToCurrentVersion(
    previousAnswers: IAnswer[] | null | undefined,
    previousVersion: ISuitabilityTestVersion | null | undefined,
    currentVersion: ISuitabilityTestVersion | null | undefined
): SuitabilityAnswersMatch
{
    const questionnaireChanged = previousVersion?.id != null
        && currentVersion?.id != null
        && previousVersion.id !== currentVersion.id;
    const currentQuestions = listSuitabilityQuestions(currentVersion);
    const previousById = indexQuestionsById(listSuitabilityQuestions(previousVersion));
    const currentById = indexQuestionsById(currentQuestions);
    const currentByFingerprint = indexQuestionsByFingerprint(currentQuestions);
    const answers: IAnswer[] = [];

    (previousAnswers ?? []).forEach((answer) =>
    {
        if(!answer)
        {
            return;
        }

        const question = findCurrentQuestion(answer, previousById, currentById, currentByFingerprint);
        if(!question)
        {
            return;
        }

        const matched = toCurrentAnswer(answer, question);
        if(matched)
        {
            answers.push(matched);
        }
    });

    return{
        answers: answers,
        questionnaireChanged: questionnaireChanged,
    };
}

function listSuitabilityQuestions(version: ISuitabilityTestVersion | null | undefined): IQuestion[]
{
    const questions: IQuestion[] = [];
    (version?.test?.blocks ?? []).forEach((block) =>
    {
        (block?.subBlock ?? []).forEach((subBlock) =>
        {
            (subBlock?.questions ?? []).forEach((question) =>
            {
                if(question?._id)
                {
                    questions.push(question);
                }
            });
        });
    });
    return questions;
}

function indexQuestionsById(questions: IQuestion[]): Map<string, IQuestion>
{
    const byId = new Map<string, IQuestion>();
    questions.forEach((question) =>
    {
        byId.set(question._id, question);
    });
    return byId;
}

function questionFingerprint(question: IQuestion): string
{
    const optionTexts = (question.answers ?? [])
        .map((option) => `${option.order}:${option.text ?? ""}`)
        .join("|");
    const rowLabels = (question.table?.rows ?? [])
        .map((row) => row.cols?.[0]?.text ?? "")
        .join("|");
    return[
        question.questionType ?? "",
        question.key ?? "",
        question.title ?? "",
        question.text ?? "",
        optionTexts,
        rowLabels,
    ].join("\u001f");
}

function indexQuestionsByFingerprint(questions: IQuestion[]): Map<string, IQuestion[]>
{
    const byFingerprint = new Map<string, IQuestion[]>();
    questions.forEach((question) =>
    {
        const fingerprint = questionFingerprint(question);
        const group = byFingerprint.get(fingerprint) ?? [];
        group.push(question);
        byFingerprint.set(fingerprint, group);
    });
    return byFingerprint;
}

function findCurrentQuestion(
    answer: IAnswer,
    previousById: Map<string, IQuestion>,
    currentById: Map<string, IQuestion>,
    currentByFingerprint: Map<string, IQuestion[]>
): IQuestion | null
{
    const sourceId = answer.originalQuestionId || answer.questionId;
    const previous = sourceId ? previousById.get(sourceId) : undefined;
    if(previous)
    {
        const sameId = currentById.get(previous._id);
        if(sameId)
        {
            return sameId;
        }

        const sameShape = currentByFingerprint.get(questionFingerprint(previous)) ?? [];
        return sameShape.length === 1 ? sameShape[0] : null;
    }

    return sourceId ? currentById.get(sourceId) ?? null : null;
}

function toNumber(value: unknown): number | null
{
    if(value == null || value === "")
    {
        return null;
    }

    const number = Number(value);
    return Number.isFinite(number) ? number : null;
}

function findOption(question: IQuestion, answer: IAnswer): IQuestionAnswer | null
{
    const options = question.answers ?? [];
    if(answer.answerId)
    {
        const byId = options.find((option) => option._id === answer.answerId);
        if(byId)
        {
            return byId;
        }
    }

    // En multi, res guarda el order de la opción. value deja de ser el de la opción en cuanto hay inputRes.
    if(question.questionType === "multi")
    {
        const optionOrder = toNumber(answer.res);
        if(optionOrder == null)
        {
            return null;
        }

        const matches = options.filter((option) => option.order === optionOrder);
        return matches.length === 1 ? matches[0] : null;
    }

    const optionOrder = toNumber(answer.order);
    const optionValue = toNumber(answer.res);
    if(optionOrder == null || optionValue == null)
    {
        return null;
    }

    const matches = options.filter((option) => option.order === optionOrder && option.value === optionValue);
    return matches.length === 1 ? matches[0] : null;
}

function findTableColumn(question: IQuestion, answer: IAnswer): IQuestionTableCol | null
{
    const columns = (question.table?.rows ?? []).flatMap((row) => row.cols ?? []);
    const byId = columns.find((column) => column._id === answer.questionId);
    if(byId)
    {
        return byId;
    }

    const columnValue = toNumber(answer.res);
    const columnOrder = toNumber(answer.colOrder);
    const matches = columns.filter((column) =>
    {
        const sameProduct = answer.colKey != null && answer.colKey !== "" && column.productKey === answer.colKey;
        const sameValue = columnValue != null && column.value === columnValue;
        const sameOrder = columnOrder == null || column.order === columnOrder;
        return sameProduct && sameValue && sameOrder;
    });
    return matches.length === 1 ? matches[0] : null;
}

function toCurrentAnswer(answer: IAnswer, question: IQuestion): IAnswer | null
{
    if(question.questionType === "table")
    {
        const column = findTableColumn(question, answer);
        if(!column)
        {
            return null;
        }

        return{
            ...answer,
            originalQuestionId: question._id,
            questionId: column._id,
            res: column.value,
            colKey: column.productKey ?? answer.colKey,
            colOrder: column.order,
            key: question.table?.key ?? answer.key,
            secondaryKey: question.table?.secondaryKey ?? answer.secondaryKey,
        };
    }

    const option = findOption(question, answer);
    if(!option)
    {
        return null;
    }

    if(question.questionType === "multi")
    {
        const inputRes = toNumber(answer.inputRes);
        return{
            ...answer,
            questionId: question._id,
            answerId: option._id,
            key: question.key,
            res: option.order,
            inputRes: inputRes ?? undefined,
            value: inputRes != null ? option.value * inputRes / 100 : option.value,
            constraintKey: question.constraintKey ?? answer.constraintKey,
        };
    }

    if(question.questionType !== "radio" && question.questionType !== "checkbox")
    {
        return null;
    }

    return{
        ...answer,
        questionId: question._id,
        key: question.key,
        res: option.value,
        order: String(option.order),
        product: question.product ?? answer.product,
        constraintKey: question.constraintKey ?? answer.constraintKey,
        secondaryKey: question.secondaryKey ?? answer.secondaryKey,
    };
}
